/** Patients bundle — submenu in sidebar (routes: `/report/bundles/patients/<pathSegment>`). */
export type ReportsPatientsSubmenuItem = {
  id: string
  label: string
  pathSegment: string
}

export const REPORTS_PATIENTS_SUBMENU: ReportsPatientsSubmenuItem[] = [
  {
    id: 'patient-base-health',
    label: 'Patient Base Health',
    pathSegment: 'patient-base-health',
  },
  {
    id: 'patient-value-rev-distribution',
    label: 'Patient Value & Rev. Distribution',
    pathSegment: 'patient-value-rev-distribution',
  },
  {
    id: 'patient-risk-compliance',
    label: 'Patient Risk & Compliance',
    pathSegment: 'patient-risk-compliance',
  },
  {
    id: 'family-opportunity-intelligence',
    label: 'Family & Opportunity Intelligence',
    pathSegment: 'family-opportunity-intelligence',
  },
]

export type ReportsEngagementSubmenuItem = {
  id: string
  label: string
  pathSegment: string
}

export const REPORTS_ENGAGEMENT_SUBMENU: ReportsEngagementSubmenuItem[] = [
  {
    id: 'recall-reactivation-control',
    label: 'Recall & Reactivation Control',
    pathSegment: 'recall-reactivation-control',
  },
  {
    id: 'schedule-reliability',
    label: 'Schedule Reliability',
    pathSegment: 'schedule-reliability',
  },
  {
    id: 'treatment-conversion-funnel',
    label: 'Treatment Conversion Funnel',
    pathSegment: 'treatment-conversion-funnel',
  },
  {
    id: 'communication-center-performance',
    label: 'Communication Center Performance',
    pathSegment: 'communication-center-performance',
  },
  // TODO: unhide Referral & Reputation when ready for implementation
  // {
  //   id: 'referral-reputation',
  //   label: 'Referral & Reputation',
  //   pathSegment: 'referral-reputation',
  // },
  // TODO: unhide Marketing Campaign Performance when ready for implementation
  // {
  //   id: 'marketing-campaign-performance',
  //   label: 'Marketing Campaign Performance',
  //   pathSegment: 'marketing-campaign-performance',
  // },
]

export type ReportsOperationsSubmenuItem = {
  id: string
  label: string
  pathSegment: string
}

export const REPORTS_OPERATIONS_SUBMENU: ReportsOperationsSubmenuItem[] = [
  {
    id: 'scheduled-appointments',
    label: 'Scheduled Appointments',
    pathSegment: 'scheduled-appointments',
  },
  {
    id: 'chair-room-utilization',
    label: 'Chair & Room Utilization',
    pathSegment: 'chair-room-utilization',
  },
  {
    id: 'hygiene-performance',
    label: 'Hygiene Performance',
    pathSegment: 'hygiene-performance',
  },
  // TODO: unhide Operational Backlog & Tasks when ready for implementation
  // {
  //   id: 'operational-backlog-tasks',
  //   label: 'Operational Backlog & Tasks',
  //   pathSegment: 'operational-backlog-tasks',
  // },
]

export type ReportsFinanceSubmenuItem = {
  id: string
  label: string
  pathSegment: string
}

export const REPORTS_FINANCE_SUBMENU: ReportsFinanceSubmenuItem[] = [
  {
    id: 'revenue-realization-analytics',
    label: 'Revenue Realization Analytics',
    pathSegment: 'revenue-realization-analytics',
  },
  {
    id: 'receivables-risk-analysis',
    label: 'Receivables Risk Analysis',
    pathSegment: 'receivables-risk-analysis',
  },
  {
    id: 'claims-performance',
    label: 'Claims Performance',
    pathSegment: 'claims-performance',
  },
  {
    id: 'goal-attainment-variance',
    label: 'Goal Attainment & Variance',
    pathSegment: 'goal-attainment-variance',
  },
]

export type ReportsProviderPerformanceSubmenuItem = {
  id: string
  label: string
  pathSegment: string
}

export const REPORTS_PROVIDER_PERFORMANCE_SUBMENU: ReportsProviderPerformanceSubmenuItem[] =
  [
    {
      id: 'production-performance',
      label: 'Production Performance',
      pathSegment: 'production-performance',
    },
    {
      id: 'collections-financial-efficiency',
      label: 'Collections & Financial Efficiency',
      pathSegment: 'collections-financial-efficiency',
    },
    {
      id: 'clinical-mix-treatment-profile',
      label: 'Clinical Mix & Treatment Profile',
      pathSegment: 'clinical-mix-treatment-profile',
    },
    {
      id: 'treatment-effectiveness',
      label: 'Treatment Effectiveness',
      pathSegment: 'treatment-effectiveness',
    },
    {
      id: 'efficiency-scorecard',
      label: 'Efficiency Scorecard',
      pathSegment: 'efficiency-scorecard',
    },
  ]

export function getPatientsSubmenuLabel(
  pathSegment: string | undefined,
): string | null {
  if (!pathSegment) return null
  const row = REPORTS_PATIENTS_SUBMENU.find(
    (i) => i.pathSegment === pathSegment,
  )
  return row?.label ?? null
}

export function getEngagementSubmenuLabel(
  pathSegment: string | undefined,
): string | null {
  if (!pathSegment) return null
  const row = REPORTS_ENGAGEMENT_SUBMENU.find(
    (i) => i.pathSegment === pathSegment,
  )
  return row?.label ?? null
}

export function getOperationsSubmenuLabel(
  pathSegment: string | undefined,
): string | null {
  if (!pathSegment) return null
  const row = REPORTS_OPERATIONS_SUBMENU.find(
    (i) => i.pathSegment === pathSegment,
  )
  return row?.label ?? null
}

export function getFinanceSubmenuLabel(
  pathSegment: string | undefined,
): string | null {
  if (!pathSegment) return null
  const row = REPORTS_FINANCE_SUBMENU.find((i) => i.pathSegment === pathSegment)
  return row?.label ?? null
}

export function getProviderPerformanceSubmenuLabel(
  pathSegment: string | undefined,
): string | null {
  if (!pathSegment) return null
  const row = REPORTS_PROVIDER_PERFORMANCE_SUBMENU.find(
    (i) => i.pathSegment === pathSegment,
  )
  return row?.label ?? null
}

/** Sidebar: categories under “Bundles” (order matches Figma). */
export const REPORTS_SIDEBAR_BUNDLE_ITEMS: { id: string; label: string }[] = [
  { id: 'patients', label: 'Patients' },
  { id: 'engagement', label: 'Engagement' },
  { id: 'operations', label: 'Operations' },
  { id: 'provider-performance', label: 'Provider Performance' },
  { id: 'finance', label: 'Finance' },
]

const REPORTS_BUNDLE_SUBMENUS: Record<
  string,
  ReadonlyArray<{ pathSegment: string }>
> = {
  patients: REPORTS_PATIENTS_SUBMENU,
  engagement: REPORTS_ENGAGEMENT_SUBMENU,
  operations: REPORTS_OPERATIONS_SUBMENU,
  'provider-performance': REPORTS_PROVIDER_PERFORMANCE_SUBMENU,
  finance: REPORTS_FINANCE_SUBMENU,
}

export function getFirstBundleReportPath(bundleId: string): string | null {
  const firstReport = REPORTS_BUNDLE_SUBMENUS[bundleId]?.[0]
  return firstReport
    ? `/report/bundles/${bundleId}/${firstReport.pathSegment}`
    : null
}

export type ReportHubCardIcon =
  | 'patients'
  | 'engagement'
  | 'operations'
  | 'provider'
  | 'financial'
  | 'custom'
  | 'saved-views'
  | 'schedules'
  | 'saved-custom'

export type ReportHubBundleCard = {
  id: string
  title: string
  description: string
  footerLabel: string
  icon: ReportHubCardIcon
  /** Custom Report card uses “Create Report” CTA style */
  variant?: 'bundle' | 'create'
}

export const REPORT_HUB_BUNDLE_CARDS: ReportHubBundleCard[] = [
  {
    id: 'patients',
    title: 'Patients',
    description: 'Track retention, growth, demographics, LTV',
    footerLabel: '4 Report Bundles',
    icon: 'patients',
  },
  {
    id: 'engagement',
    title: 'Engagement & Growth',
    description: 'Marketing, Conversions, Communications',
    footerLabel: '4 Report Bundles',
    icon: 'engagement',
  },
  {
    id: 'operations',
    title: 'Operations',
    description: 'Schedule, hygiene, chair, utilization',
    footerLabel: '4 Report Bundles',
    icon: 'operations',
  },
  {
    id: 'provider-performance',
    title: 'Provider Performance',
    description: 'Production, efficiency, case acceptance',
    footerLabel: '5 Report Bundles',
    icon: 'provider',
  },
  {
    id: 'finance',
    title: 'Financial',
    description: 'AR, collections, claims, goals',
    footerLabel: '4 Report Bundles',
    icon: 'financial',
  },
  {
    id: 'custom-report',
    title: 'Custom Report',
    description: 'Build custom reports',
    footerLabel: 'Create Report',
    icon: 'custom',
    variant: 'create',
  },
]

export type ReportHubPresetCard = {
  id: string
  title: string
  description: string
  footerLabel: string
  icon: ReportHubCardIcon
}

export const REPORT_HUB_PRESET_CARDS: ReportHubPresetCard[] = [
  {
    id: 'saved-views',
    title: 'Saved Views',
    description: 'Manage all saved report parameters',
    footerLabel: 'Total of 12 saved views',
    icon: 'saved-views',
  },
  {
    id: 'schedules',
    title: 'Report Schedules',
    description: 'Manage all saved scheduled reports',
    footerLabel: 'Total of 10 scheduled reports',
    icon: 'schedules',
  },
  {
    id: 'saved-custom',
    title: 'Saved Custom Reports',
    description: 'Manage all saved customized reports',
    footerLabel: 'Total of 10 customized reports',
    icon: 'saved-custom',
  },
]
